import React, { useEffect, useMemo, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
} from "@mui/material";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import isGHMC, { tableRowBg } from "../helper/helpar";
import { TableLoader } from "../ShimmerLoader";

const PAGINATION_COLORS = {
    navy: "#003366",
    textMuted: "#6B7280",
    border: "#E0E0E0",
};

const MUITable = ({
    needSRNumber = true,
    columns,
    rows,
    tableHeight = 400,
    hasTotalRow = false,
    serverPagination = false,
    totalCount = 0,
    page: externalPage,
    rowsPerPage: externalRowsPerPage,
    onPageChange: externalOnPageChange,
    onRowsPerPageChange: externalOnRowsPerPageChange,
    rowsPerPageOptions,
    loading = false,
    hasExpandButton = false,
    expandContent = null,
}) => {
    const defaultRowsPerPage = serverPagination ? 10 : 100;
    const defaultPageSizeOptions = serverPagination
        ? [5, 10, 25, 50]
        : [100, 1000, 10000];
    const paginationOptions = rowsPerPageOptions ?? defaultPageSizeOptions;
    const [sortConfig, setSortConfig] = useState({ field: null, direction: "asc" });
    const [internalPage, setInternalPage] = useState(0);
    const [isLastPage, setIsLastPage] = useState(false);
    const [internalRowsPerPage, setInternalRowsPerPage] = useState(defaultRowsPerPage);
    const [expandedRows, setExpandedRows] = useState({});
    const isProd = isGHMC();
    const currentPage = serverPagination ? (externalPage ?? 0) : internalPage;
    const rowsPerPage = serverPagination
        ? (externalRowsPerPage ?? defaultRowsPerPage)
        : internalRowsPerPage;

    // Handle sorting
    const handleSort = (field) => {
        const isAsc = sortConfig.field === field && sortConfig.direction === "asc";
        setSortConfig({ field, direction: isAsc ? "desc" : "asc" });
    };

    useEffect(() => {
        if (!serverPagination) {
            setInternalPage(0);
        }
    }, [rows, serverPagination]);

    // Sort rows dynamically based on the current sort field
    const sortedRows = React.useMemo(() => {
        if (!sortConfig.field) return rows;
        return [...rows].sort((a, b) => {
            // Keep total/footer rows at the end and out of sort order
            if (a?.isTotalRow) return 1;
            if (b?.isTotalRow) return -1;

            const aValue = a[sortConfig.field];
            const bValue = b[sortConfig.field];

            // Handle string comparison case-insensitively
            if (typeof aValue === "string" && typeof bValue === "string") {
                return sortConfig.direction === "asc"
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            // Handle number comparison
            if (typeof aValue === "number" && typeof bValue === "number") {
                return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
            }

            // Fallback for other types
            return 0;
        });
    }, [rows, sortConfig]);

    const dataRows = useMemo(
        () =>
            hasTotalRow
                ? (sortedRows || []).filter((row) => !row?.isTotalRow)
                : sortedRows || [],
        [sortedRows, hasTotalRow]
    );

    const footerRows = useMemo(
        () =>
            hasTotalRow
                ? (sortedRows || []).filter((row) => row?.isTotalRow)
                : [],
        [sortedRows, hasTotalRow]
    );

    const paginatedRows = serverPagination
        ? sortedRows
        : [
            ...dataRows.slice(
                currentPage * rowsPerPage,
                currentPage * rowsPerPage + rowsPerPage
            ),
            ...footerRows,
        ];

    const paginationCount = serverPagination
        ? totalCount
        : hasTotalRow
            ? dataRows.length
            : rows?.length;
    const totalPages = Math.max(1, Math.ceil((paginationCount || 0) / rowsPerPage));
    const displayPage = currentPage + 1;
    const rangeStart = paginationCount ? currentPage * rowsPerPage + 1 : 0;
    const rangeEnd = Math.min((currentPage + 1) * rowsPerPage, paginationCount || 0);

    const pageNumbers = useMemo(() => {
        const pages = [];
        const maxVisible = 6;
        for (let i = 1; i <= Math.min(totalPages, maxVisible); i += 1) {
            pages.push(i);
        }
        return pages;
    }, [totalPages]);

    const handleChangePage = (event, newPage) => {
        const lastPage = paginationCount / rowsPerPage;
        setIsLastPage(Math.floor(lastPage) === newPage);

        if (serverPagination) {
            externalOnPageChange?.(event, newPage);
            return;
        }

        setInternalPage(newPage);
    };

    useEffect(() => {
        const lastPage = paginationCount / rowsPerPage;
        setIsLastPage(Math.floor(lastPage) === 0);
    }, [rows, paginationCount, rowsPerPage]);

    const handleChangeRowsPerPage = (event) => {
        const nextRowsPerPage = parseInt(event.target.value, 10);

        if (serverPagination) {
            externalOnRowsPerPageChange?.(event);
            return;
        }

        setInternalRowsPerPage(nextRowsPerPage);
        setInternalPage(0);
    };
    const hasGroupedHeaders = (columns || []).some(
        (column) => Array.isArray(column.children) && column.children.length > 0
    );
    // MUI's `stickyHeader` only anchors every header cell to `top: 0`, which
    // works for a single header row but makes a second (grouped/child) header
    // row overlap the first instead of sticking below it. Give each row a
    // fixed height so the second row's cells can be offset by the first row's
    // height and stick correctly underneath it.
    const GROUP_HEADER_ROW_HEIGHT = 32;
    const headerStyles = {
        textAlign: "center",
        border: "none",
        padding: hasGroupedHeaders ? "4px 6px" : "8px 6px",
        color: "#fff",
        backgroundColor: isProd ? "#004554" : "#EC1117",
        fontSize: hasGroupedHeaders ? "11px" : "12px",
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: "normal",
        verticalAlign: "middle",
        ...(hasGroupedHeaders && {
            position: "sticky",
            top: 0,
            zIndex: 3,
        }),
    };
    // Only the (non-rowSpan) group-label cell needs a fixed height — it fixes
    // row 1's rendered height so row 2 can be reliably offset below it.
    // Cells with rowSpan={2} are left to size naturally across both rows.
    const groupHeaderCellStyles = hasGroupedHeaders
        ? { ...headerStyles, height: GROUP_HEADER_ROW_HEIGHT }
        : headerStyles;
    const childHeaderStyles = hasGroupedHeaders
        ? {
              ...headerStyles,
              top: GROUP_HEADER_ROW_HEIGHT,
              zIndex: 2,
          }
        : headerStyles;
    const cellStyles = {
        textAlign: "center",
        border: "none",
        borderBottom: "1px solid #eef0f3",
        padding: "8px 6px",
        fontSize: "13px",
        // One line per value — columns size themselves to their data and the
        // table scrolls sideways when it's wider than the page.
        whiteSpace: "nowrap",
        verticalAlign: "middle",
    };
    const flatColumns = useMemo(
        () =>
            (columns || []).flatMap((column) =>
                Array.isArray(column.children) && column.children.length > 0
                    ? column.children
                    : [column]
            ),
        [columns]
    );
    const totalColumnCount = flatColumns.length + (needSRNumber ? 1 : 0) + (hasExpandButton ? 1 : 0);

    const toggleRowExpansion = (rowKey) => {
        setExpandedRows((prev) => ({
            ...prev,
            [rowKey]: !prev[rowKey],
        }));
    };

    if (loading) {
        return <TableLoader height={tableHeight} columns={flatColumns?.length || 5} />;
    }

    return (
        <Paper>
            <TableContainer component={Paper}
                className={hasTotalRow ? "MUITable-hasTotalRow" : ""}
                sx={{
                    width: '100%',
                    maxHeight: tableHeight,
                }}
            >
                <Table stickyHeader size="small">
                    <TableHead className={`${isProd ? 'bg-ghmc' : 'bg-swm'}`}>
                        <TableRow>
                            {hasExpandButton && (
                                <TableCell
                                    sx={headerStyles}
                                    rowSpan={hasGroupedHeaders ? 2 : 1}
                                    style={{ width: 52 }}
                                >
                                    
                                </TableCell>
                            )}
                            {needSRNumber && (
                                <TableCell
                                    sx={headerStyles}
                                    rowSpan={hasGroupedHeaders ? 2 : 1}
                                >
                                    S.No
                                </TableCell>
                            )}
                            {columns.map((column, columnIndex) => {
                                const childColumns = Array.isArray(column.children)
                                    ? column.children
                                    : [];
                                const hasChildren = childColumns.length > 0;

                                if (hasChildren) {
                                    return (
                                        <TableCell
                                            sx={groupHeaderCellStyles}
                                            key={`group-${columnIndex}`}
                                            align="center"
                                            colSpan={childColumns.length}
                                        >
                                            {column.label}
                                        </TableCell>
                                    );
                                }

                                return (
                                    <TableCell
                                        sx={headerStyles}
                                        key={columnIndex}
                                        align={column.align || "left"}
                                        rowSpan={hasGroupedHeaders ? 2 : 1}
                                        style={{ minWidth: column.width || "auto" }}
                                    >
                                        {column.isSortable ? (
                                            <TableSortLabel
                                                active={sortConfig.field === column.field}
                                                direction={
                                                    sortConfig.field === column.field
                                                        ? sortConfig.direction
                                                        : "asc"
                                                }
                                                onClick={() => handleSort(column.field)}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        ) : (
                                            column.label
                                        )}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                        {hasGroupedHeaders && (
                            <TableRow>
                                {columns.flatMap((column, columnIndex) => {
                                    const childColumns = Array.isArray(column.children)
                                        ? column.children
                                        : [];
                                    if (childColumns.length === 0) return [];

                                    return childColumns.map((childColumn, childIndex) => (
                                        <TableCell
                                            sx={childHeaderStyles}
                                            key={`child-${columnIndex}-${childIndex}`}
                                            align={childColumn.align || "left"}
                                            style={{ minWidth: childColumn.width || "auto" }}
                                        >
                                            {childColumn.isSortable ? (
                                                <TableSortLabel
                                                    active={
                                                        sortConfig.field === childColumn.field
                                                    }
                                                    direction={
                                                        sortConfig.field === childColumn.field
                                                            ? sortConfig.direction
                                                            : "asc"
                                                    }
                                                    onClick={() => handleSort(childColumn.field)}
                                                >
                                                    {childColumn.label}
                                                </TableSortLabel>
                                            ) : (
                                                childColumn.label
                                            )}
                                        </TableCell>
                                    ));
                                })}
                            </TableRow>
                        )}
                    </TableHead>
                    <TableBody>
                        {paginatedRows?.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={totalColumnCount}
                                    align="center"
                                >
                                    No data available
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedRows?.map((row, rowIndex) => {
                                const rowKey = row?.id ?? row?.employeeId ?? row?.empCode ?? rowIndex;
                                const isExpanded = !!expandedRows[rowKey];

                                return (
                                    <React.Fragment key={rowKey}>
                                        <TableRow
                                            sx={{
                                                backgroundColor: tableRowBg(rowIndex),
                                            }}
                                            className={isLastPage ? "Last-page" : ""}
                                        >
                                            {hasExpandButton && (
                                                <TableCell sx={cellStyles} style={{ width: 52 }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleRowExpansion(rowKey)}
                                                        aria-label={isExpanded ? "Collapse row" : "Expand row"}
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: 20,
                                                            height: 20,
                                                            borderRadius: 4,
                                                            border: `1px solid ${isExpanded ? "#F5A623" : "#D1D5DB"}`,
                                                            backgroundColor: "#fff",
                                                            color: isExpanded ? "#F5A623" : "#6B7280",
                                                            fontSize: "14px",
                                                            fontWeight: 700,
                                                            cursor: "pointer",
                                                            lineHeight: 1,
                                                            padding: 0,
                                                        }}
                                                    >
                                                        {isExpanded ? "−" : "+"}
                                                    </button>
                                                </TableCell>
                                            )}
                                            {needSRNumber && <TableCell sx={cellStyles}>
                                                <span>
                                                    {row?.isTotalRow
                                                        ? ""
                                                        : currentPage * rowsPerPage + rowIndex + 1}
                                                </span>
                                            </TableCell>}
                                            {flatColumns.map((column, columnIndex) => (
                                                <TableCell
                                                    sx={cellStyles}
                                                    key={columnIndex}
                                                    align={column.align || "left"}
                                                    style={{ minWidth: column.width || "auto" }}
                                                >
                                                    <span>
                                                        {column.render
                                                            ? column.render(row, rowIndex)
                                                            : row[column.field]
                                                                ? row[column.field]
                                                                : "N/A"}
                                                    </span>
                                                </TableCell>
                                            ))}
                                        </TableRow>

                                        {isExpanded && expandContent && (
                                            <TableRow sx={{ backgroundColor: "#F1F5F9" }}>
                                                <TableCell
                                                    colSpan={totalColumnCount}
                                                    sx={{
                                                        padding: "16px 24px",
                                                        border: "none",
                                                        borderBottom: "1px solid #eef0f3",
                                                    }}
                                                >
                                                    {expandContent(row, rowIndex)}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <div
                style={{
                    marginTop: 16,
                    padding: "12px 16px",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                }}
            >
                <p style={{ margin: 0, fontSize: "14px", color: PAGINATION_COLORS.textMuted }}>
                    Showing {rangeStart}-{rangeEnd} of {paginationCount || 0} results
                </p>

                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 20,
                    }}
                >
                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: "14px",
                            color: PAGINATION_COLORS.textMuted,
                            margin: 0,
                        }}
                    >
                        Row per page
                        <select
                            value={rowsPerPage}
                            onChange={handleChangeRowsPerPage}
                            style={{
                                appearance: "none",
                                borderRadius: 6,
                                border: `1px solid ${PAGINATION_COLORS.border}`,
                                backgroundColor: "#F0F4F8",
                                padding: "6px 28px 6px 12px",
                                fontSize: "14px",
                                color: "#374151",
                                outline: "none",
                            }}
                        >
                            {paginationOptions.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </label>

                    <nav
                        style={{ display: "flex", alignItems: "center", gap: 6 }}
                        aria-label="Pagination"
                    >
                        <button
                            type="button"
                            onClick={() =>
                                handleChangePage(null, Math.max(0, currentPage - 1))
                            }
                            disabled={displayPage === 1}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 32,
                                height: 32,
                                border: "none",
                                background: "transparent",
                                color: "#9CA3AF",
                                cursor: displayPage === 1 ? "not-allowed" : "pointer",
                                opacity: displayPage === 1 ? 0.4 : 1,
                            }}
                            aria-label="Previous page"
                        >
                            <FiChevronLeft size={16} />
                        </button>

                        {pageNumbers.map((page) => (
                            <button
                                key={page}
                                type="button"
                                onClick={() => handleChangePage(null, page - 1)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: 32,
                                    height: 32,
                                    borderRadius: 6,
                                    padding: "0 10px",
                                    border: "none",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    backgroundColor:
                                        displayPage === page
                                            ? PAGINATION_COLORS.navy
                                            : "#F0F4F8",
                                    color: displayPage === page ? "#FFFFFF" : "#374151",
                                }}
                                aria-current={displayPage === page ? "page" : undefined}
                            >
                                {page}
                            </button>
                        ))}

                        <button
                            type="button"
                            onClick={() =>
                                handleChangePage(
                                    null,
                                    Math.min(totalPages - 1, currentPage + 1)
                                )
                            }
                            disabled={displayPage === totalPages}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 32,
                                height: 32,
                                border: "none",
                                background: "transparent",
                                color: "#9CA3AF",
                                cursor:
                                    displayPage === totalPages ? "not-allowed" : "pointer",
                                opacity: displayPage === totalPages ? 0.4 : 1,
                            }}
                            aria-label="Next page"
                        >
                            <FiChevronRight size={16} />
                        </button>
                    </nav>
                </div>
            </div>
        </Paper>
    );
};

export default MUITable;
